import { api } from './axios';

export type StorePurchasePayment = 'CASH' | 'CREDIT';
export type StoreSalePayment = 'CASH' | 'CREDIT';
export type StoreCustomerType = 'STUDENT' | 'WALK_IN';
export type AssetCondition = 'NEW' | 'GOOD' | 'FAIR' | 'POOR' | 'DAMAGED';
export type AssetStatus = 'IN_USE' | 'IN_STORAGE' | 'DISPOSED';

export interface Vendor {
  id: number;
  name: string;
  vendorTypeId: number;
  vendorTypeName: string | null;
  contactPerson: string | null;
  phone: string | null;
  address: string | null;
  cnicOrNtn: string | null;
  bankName: string | null;
  bankAccountNumber: string | null;
  openingBalance: string;
  openingBalanceDate: string | null;
  creditPurchases: string;
  totalPaid: string;
  payable: string;
}

export interface VendorInput {
  name: string;
  vendorTypeId: number;
  contactPerson?: string;
  phone?: string;
  address?: string;
  cnicOrNtn?: string;
  bankName?: string;
  bankAccountNumber?: string;
  openingBalance?: string | number;
  openingBalanceDate?: string | null;
}

export interface VendorPayment {
  id: number;
  vendorId: number;
  vendorName: string | null;
  paymentDate: string;
  amount: string;
  bankAccountId: number;
  bankAccountName: string | null;
  reference: string | null;
  remarks: string | null;
  reversed: boolean;
  reversalReason: string | null;
  createdAt: string;
}

export interface VendorAgingRow {
  vendorId: number;
  vendorName: string;
  vendorTypeName: string | null;
  current: string;        // 0–30 days
  days31to60: string;
  days61to90: string;
  over90: string;
  total: string;
  oldestOpenInvoiceDate: string | null;
  oldestOpenAgeDays: number | null;
}

export interface VendorAgingReport {
  asOf: string;
  totalCurrent: string;
  total31to60: string;
  total61to90: string;
  totalOver90: string;
  grandTotal: string;
  rows: VendorAgingRow[];
}

export interface VendorLedgerRow {
  date: string;
  type: string;
  reference: string | null;
  description: string | null;
  debit: string;
  credit: string;
  runningPayable: string;
}

export interface VendorLedger {
  vendorId: number;
  vendorName: string;
  from: string;
  to: string;
  openingPayable: string;
  closingPayable: string;
  totalDebits: string;
  totalCredits: string;
  rows: VendorLedgerRow[];
}

export interface VendorPaymentInput {
  vendorId: number;
  paymentDate: string;
  amount: string;
  bankAccountId: number;
  reference?: string;
  remarks?: string;
}

export interface StoreItem {
  id: number;
  name: string;
  itemTypeId: number;
  itemTypeName: string | null;
  classApplicability: number[] | null;
  costPrice: string;
  sellingPrice: string;
  currentStock: string;
  openingStock: string;
  reorderLevel: string;
  lowStock: boolean;
}

export interface StoreItemInput {
  name: string;
  itemTypeId: number;
  classApplicability?: number[];
  sellingPrice: string;
  openingStock: string;
  reorderLevel: string;
}

export interface StorePurchase {
  id: number;
  storeItemId: number;
  storeItemName: string | null;
  vendorId: number;
  vendorName: string | null;
  purchaseDate: string;
  quantity: string;
  unitCost: string;
  totalCost: string;
  paymentType: StorePurchasePayment;
  bankAccountId: number | null;
  bankAccountName: string | null;
  reversed: boolean;
  reversalReason: string | null;
  createdAt: string;
}

export interface StorePurchaseInput {
  storeItemId: number;
  vendorId: number;
  purchaseDate: string;
  quantity: string;
  unitCost: string;
  paymentType: StorePurchasePayment;
  bankAccountId?: number | null;
}

export interface StoreSaleLine {
  id: number;
  storeItemId: number;
  storeItemName: string | null;
  quantity: string;
  unitPrice: string;
  lineTotal: string;
}

export interface StoreSale {
  id: number;
  receiptNumber: number;
  saleDate: string;
  customerType: StoreCustomerType;
  enrollmentId: number | null;
  studentName: string | null;
  studentRegistrationNumber: string | null;
  walkInName: string | null;
  totalAmount: string;
  discountAmount: string;
  netAmount: string;
  paidAmount: string;
  outstanding: string;
  paymentType: StoreSalePayment;
  bankAccountId: number | null;
  bankAccountName: string | null;
  reversed: boolean;
  reversalReason: string | null;
  createdAt: string;
  lines: StoreSaleLine[];
}

export interface StoreSaleInput {
  saleDate: string;
  customerType: StoreCustomerType;
  enrollmentId?: number | null;
  walkInName?: string;
  lines: { storeItemId: number; quantity: string; unitPriceOverride?: string }[];
  discountAmount: string;
  paymentType: StoreSalePayment;
  bankAccountId?: number | null;
  paidNow?: string;
}

export interface StoreCreditPayment {
  id: number;
  saleId: number;
  receiptNumber: number;
  paymentDate: string;
  amount: string;
  bankAccountId: number;
  bankAccountName: string | null;
  createdAt: string;
}

export interface AssetMovement {
  id: number;
  assetId: number;
  assignedToStaffId: number | null;
  assignedToStaffName: string | null;
  assignedToLocation: string | null;
  assignedAt: string;
  returnedAt: string | null;
  remarks: string | null;
}

export interface Asset {
  id: number;
  assetCode: string;
  name: string;
  assetTypeId: number;
  assetTypeName: string | null;
  description: string | null;
  purchaseDate: string | null;
  purchaseCost: string | null;
  supplierName: string | null;
  location: string | null;
  condition: AssetCondition;
  status: AssetStatus;
  photoPath: string | null;
  disposalDate: string | null;
  disposalReason: string | null;
  disposalValue: string | null;
  currentAssignment: AssetMovement | null;
  movements: AssetMovement[];
}

export interface AssetInput {
  assetCode: string;
  name: string;
  assetTypeId: number;
  description?: string;
  purchaseDate?: string | null;
  purchaseCost?: string;
  supplierName?: string;
  location?: string;
  photoPath?: string | null;
  condition: AssetCondition;
}

export interface InventoryItem {
  id: number;
  name: string;
  itemTypeId: number;
  itemTypeName: string | null;
  unit: string;
  openingBalance: string;
  currentStock: string;
  lowStockThreshold: string;
  lowStock: boolean;
}

export interface InventoryItemInput {
  name: string;
  itemTypeId: number;
  unit: string;
  openingBalance: string;
  lowStockThreshold: string;
}

export interface InventoryPurchase {
  id: number;
  inventoryItemId: number;
  inventoryItemName: string | null;
  vendorId: number | null;
  vendorName: string | null;
  purchaseDate: string;
  quantity: string;
  unitCost: string | null;
  totalCost: string | null;
  paymentType: 'CASH' | 'CREDIT' | null;
  bankAccountId: number | null;
  bankAccountName: string | null;
  expenseAccountId: number | null;
  expenseAccountCode: string | null;
  remarks: string | null;
  createdAt: string;
}

export interface InventoryPurchaseInput {
  inventoryItemId: number;
  vendorId?: number | null;
  purchaseDate: string;
  quantity: string;
  unitCost?: string | null;
  paymentType: 'CASH' | 'CREDIT';
  bankAccountId?: number | null;
  expenseAccountId: number;
  remarks?: string;
}

export interface InventoryIssue {
  id: number;
  inventoryItemId: number;
  inventoryItemName: string | null;
  issuedToStaffId: number | null;
  issuedToStaffName: string | null;
  issuedToDescription: string | null;
  issueDate: string;
  quantity: string;
  purpose: string;
  createdAt: string;
}

export const inventoryApi = {
  // vendors
  listVendors: () => api.get<Vendor[]>('/inventory/vendors').then((r) => r.data),
  getVendor: (id: number) => api.get<Vendor>(`/inventory/vendors/${id}`).then((r) => r.data),
  createVendor: (body: VendorInput) => api.post<Vendor>('/inventory/vendors', body).then((r) => r.data),
  updateVendor: (id: number, body: VendorInput) =>
    api.put<Vendor>(`/inventory/vendors/${id}`, body).then((r) => r.data),
  deleteVendor: (id: number) => api.delete<void>(`/inventory/vendors/${id}`).then((r) => r.data),
  vendorAging: (asOf?: string) =>
    api
      .get<VendorAgingReport>('/inventory/vendors/aging', { params: asOf ? { asOf } : undefined })
      .then((r) => r.data),
  vendorLedger: (vendorId: number, from: string, to: string) =>
    api
      .get<VendorLedger>(`/inventory/vendors/${vendorId}/ledger`, { params: { from, to } })
      .then((r) => r.data),

  // vendor payments
  listVendorPayments: (from: string, to: string) =>
    api.get<VendorPayment[]>('/inventory/vendor-payments', { params: { from, to } }).then((r) => r.data),
  paymentsForVendor: (id: number) =>
    api.get<VendorPayment[]>(`/inventory/vendors/${id}/payments`).then((r) => r.data),
  addVendorPayment: (body: VendorPaymentInput) =>
    api.post<VendorPayment>('/inventory/vendor-payments', body).then((r) => r.data),
  reverseVendorPayment: (id: number, reason: string) =>
    api.post<VendorPayment>(`/inventory/vendor-payments/${id}/reverse`, { reason }).then((r) => r.data),

  // store items
  listStoreItems: () => api.get<StoreItem[]>('/inventory/store/items').then((r) => r.data),
  storeLowStock: () => api.get<StoreItem[]>('/inventory/store/items/low-stock').then((r) => r.data),
  getStoreItem: (id: number) => api.get<StoreItem>(`/inventory/store/items/${id}`).then((r) => r.data),
  createStoreItem: (body: StoreItemInput) =>
    api.post<StoreItem>('/inventory/store/items', body).then((r) => r.data),
  updateStoreItem: (id: number, body: StoreItemInput) =>
    api.put<StoreItem>(`/inventory/store/items/${id}`, body).then((r) => r.data),
  deleteStoreItem: (id: number) =>
    api.delete<void>(`/inventory/store/items/${id}`).then((r) => r.data),

  // store purchases
  listStorePurchases: (from: string, to: string) =>
    api.get<StorePurchase[]>('/inventory/store/purchases', { params: { from, to } }).then((r) => r.data),
  purchasesForItem: (id: number) =>
    api.get<StorePurchase[]>(`/inventory/store/items/${id}/purchases`).then((r) => r.data),
  addStorePurchase: (body: StorePurchaseInput) =>
    api.post<StorePurchase>('/inventory/store/purchases', body).then((r) => r.data),
  reverseStorePurchase: (id: number, reason: string) =>
    api.post<StorePurchase>(`/inventory/store/purchases/${id}/reverse`, { reason }).then((r) => r.data),

  // store sales
  listStoreSales: (from: string, to: string) =>
    api.get<StoreSale[]>('/inventory/store/sales', { params: { from, to } }).then((r) => r.data),
  outstandingSales: () =>
    api.get<StoreSale[]>('/inventory/store/sales/outstanding').then((r) => r.data),
  getStoreSale: (id: number) =>
    api.get<StoreSale>(`/inventory/store/sales/${id}`).then((r) => r.data),
  addStoreSale: (body: StoreSaleInput) =>
    api.post<StoreSale>('/inventory/store/sales', body).then((r) => r.data),
  reverseStoreSale: (id: number, reason: string) =>
    api.post<StoreSale>(`/inventory/store/sales/${id}/reverse`, { reason }).then((r) => r.data),
  payCredit: (body: { saleId: number; paymentDate: string; amount: string; bankAccountId: number }) =>
    api.post<StoreCreditPayment>('/inventory/store/credit-payments', body).then((r) => r.data),
  listCreditPayments: (saleId: number) =>
    api.get<StoreCreditPayment[]>(`/inventory/store/sales/${saleId}/credit-payments`).then((r) => r.data),

  // assets
  listAssets: (params: { status?: AssetStatus; assetTypeId?: number; q?: string }) =>
    api.get<Asset[]>('/inventory/assets', { params }).then((r) => r.data),
  getAsset: (id: number) => api.get<Asset>(`/inventory/assets/${id}`).then((r) => r.data),
  createAsset: (body: AssetInput) => api.post<Asset>('/inventory/assets', body).then((r) => r.data),
  updateAsset: (id: number, body: AssetInput) =>
    api.put<Asset>(`/inventory/assets/${id}`, body).then((r) => r.data),
  assignAsset: (id: number, body: { staffId?: number; location?: string; remarks?: string }) =>
    api.post<Asset>(`/inventory/assets/${id}/assign`, body).then((r) => r.data),
  returnAsset: (id: number, remarks?: string) =>
    api.post<Asset>(`/inventory/assets/${id}/return`, null, { params: { remarks } }).then((r) => r.data),
  disposeAsset: (id: number, body: { disposalDate: string; reason: string; disposalValue?: string; bankAccountId?: number | null }) =>
    api.post<Asset>(`/inventory/assets/${id}/dispose`, body).then((r) => r.data),

  // consumables
  listConsumables: () =>
    api.get<InventoryItem[]>('/inventory/consumables/items').then((r) => r.data),
  consumablesLowStock: () =>
    api.get<InventoryItem[]>('/inventory/consumables/items/low-stock').then((r) => r.data),
  createConsumable: (body: InventoryItemInput) =>
    api.post<InventoryItem>('/inventory/consumables/items', body).then((r) => r.data),
  updateConsumable: (id: number, body: InventoryItemInput) =>
    api.put<InventoryItem>(`/inventory/consumables/items/${id}`, body).then((r) => r.data),
  deleteConsumable: (id: number) =>
    api.delete<void>(`/inventory/consumables/items/${id}`).then((r) => r.data),
  listConsumablePurchases: (from: string, to: string) =>
    api.get<InventoryPurchase[]>('/inventory/consumables/purchases', { params: { from, to } }).then((r) => r.data),
  addConsumablePurchase: (body: InventoryPurchaseInput) =>
    api.post<InventoryPurchase>('/inventory/consumables/purchases', body).then((r) => r.data),
  listConsumableIssues: (from: string, to: string) =>
    api.get<InventoryIssue[]>('/inventory/consumables/issues', { params: { from, to } }).then((r) => r.data),
  addConsumableIssue: (body: {
    inventoryItemId: number;
    issuedToStaffId?: number | null;
    issuedToDescription?: string;
    issueDate: string;
    quantity: string;
    purpose: string;
  }) => api.post<InventoryIssue>('/inventory/consumables/issues', body).then((r) => r.data),
};
